import { Gauge, Clock, TrendingUp, Mountain, Map, Image, Trophy, Calendar, MapPin } from 'lucide-react';
import { Badge } from './ui/badge';

interface RunStatsBarProps {
  distance?: number | null;
  duration?: number | null;
  averageSpeed?: number | null;
  elevationGain?: number | null;
  trailsCount?: number;
  photosCount?: number;
  waypointsCount?: number;
  achievementsCount?: number;
  eventName?: string | null;
}

const RunStatsBar = ({
  distance,
  duration,
  averageSpeed,
  elevationGain,
  trailsCount = 0,
  photosCount = 0,
  waypointsCount = 0,
  achievementsCount = 0,
  eventName
}: RunStatsBarProps) => {
  const formatDistance = (miles: number | null | undefined) => {
    if (miles === null || miles === undefined) return 'N/A';
    return `${miles.toFixed(1)} mi`;
  };

  const formatDuration = (minutes: number | null | undefined) => {
    if (minutes === null || minutes === undefined) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${remainingMinutes}m`;
    }
    return `${minutes}m`;
  };

  const formatSpeed = (speed: number | null | undefined) => {
    if (speed === null || speed === undefined) return 'N/A';
    return `${speed.toFixed(1)} mph`;
  };

  const formatElevation = (feet: number | null | undefined) => {
    if (feet === null || feet === undefined) return 'N/A';
    return `${feet.toLocaleString()} ft`;
  };

  return (
    <div className="border-y border-border bg-card/50 backdrop-blur-sm">
      <div className="container mx-auto px-4 py-4">
        <div className="flex flex-wrap items-center justify-center gap-6 md:gap-8">
          {/* Distance */}
          <div className="flex items-center gap-2">
            <Gauge className="h-5 w-5 text-primary" />
            <div>
              <p className="text-xs text-muted-foreground">Distance</p>
              <p className="text-lg font-bold text-foreground">{formatDistance(distance)}</p>
            </div>
          </div>

          {/* Duration */}
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            <div>
              <p className="text-xs text-muted-foreground">Duration</p>
              <p className="text-lg font-bold text-foreground">{formatDuration(duration)}</p>
            </div>
          </div>

          {/* Average Speed */}
          {averageSpeed && (
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              <div>
                <p className="text-xs text-muted-foreground">Avg Speed</p>
                <p className="text-lg font-bold text-foreground">{formatSpeed(averageSpeed)}</p>
              </div>
            </div>
          )}

          {/* Elevation Gain */}
          {elevationGain && (
            <div className="flex items-center gap-2">
              <Mountain className="h-5 w-5 text-primary" />
              <div>
                <p className="text-xs text-muted-foreground">Elevation</p>
                <p className="text-lg font-bold text-foreground">{formatElevation(elevationGain)}</p>
              </div>
            </div>
          )}

          {/* Trails Explored */}
          <div className="flex items-center gap-2">
            <Map className="h-5 w-5 text-primary" />
            <div>
              <p className="text-xs text-muted-foreground">Trails</p>
              <p className="text-lg font-bold text-foreground">{trailsCount}</p>
            </div>
          </div>

          {/* Photos */}
          <div className="flex items-center gap-2">
            <Image className="h-5 w-5 text-primary" />
            <div>
              <p className="text-xs text-muted-foreground">Photos</p>
              <p className="text-lg font-bold text-foreground">{photosCount}</p>
            </div>
          </div>

          {/* Waypoints */}
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-primary" />
            <div>
              <p className="text-xs text-muted-foreground">Waypoints</p>
              <p className="text-lg font-bold text-foreground">{waypointsCount}</p>
            </div>
          </div>

          {/* Achievements */}
          {achievementsCount > 0 && (
            <div className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-primary" />
              <div>
                <p className="text-xs text-muted-foreground">Badges</p>
                <p className="text-lg font-bold text-foreground">{achievementsCount}</p>
              </div>
            </div>
          )}

          {/* Event Badge */}
          {eventName && (
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              <Badge variant="secondary" className="text-sm">
                {eventName}
              </Badge>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RunStatsBar;
